import React from 'react';
import PropTypes from 'prop-types';
import ReactSelect from 'react-select';
import 'react-select/dist/react-select.css';
import CommonAppSetup from '../../../common/common-app-setup/CommonAppSetup';
import InputContainer from '../input-container/InputContainer';
import Components from '../../../common/Components';
import joinClassNames from '../../../common/JoinClassNames';
import subTypes from './SelectInputSubType';
import ChildPosition, {positionChildren} from '../ChildPosition';
import ValidationEngine from "../../../common/validation/ValidationEngine";
import ValidationType from "../../../common/validation/ValidationType";
import _ from 'lodash';
import DirtyEngine from "../../../common/dirty/DirtyEngine";

const propTypes = {
	// allow user to clear the selection
	// clearable: react-select
	allowClear: PropTypes.bool,

	// children node that allows you to pass in other elements
	children: PropTypes.node,

	// put the children before the label or after the input
	childrenPosition: PropTypes.oneOf([
		ChildPosition.AFTER,
		ChildPosition.BEFORE,
	]),

	// what extra classes should go on the input?
	className: PropTypes.string,

	// tracks dirty state of fields
	dirtyEngine: PropTypes.instanceOf(DirtyEngine),

	// if true then the input will be disabled
	// passthrough
	disabled: PropTypes.bool,

	// An error message to show. Overrides validation errors.
	errorMessage: PropTypes.string,

	// whether escape clears the value when the menu is closed
	// passthrough (set default to false instead of true)
	escapeClearsValue: PropTypes.bool,

	// the name of the redux field being edited and id of the input for label.for=
	// aria-labelledby : HTML ID of an element that should be used as the label (for assistive tech)
	// aria-label: Aria label (for assistive tech)
	// id: html id to set on the input element for accessibility or tests
	// name: field name, for hidden <input /> tag
	field: PropTypes.string.isRequired,

	// is this input "dirty" - trumps DirtyEngine
	isDirty: PropTypes.bool,

	// the label/placeholder to show
	label: PropTypes.string.isRequired,

	// label key so you can use your own key value pair
	// use with valueKey
	// default: "label"
	// passthrough
	labelKey: PropTypes.string,

	// can more than one option be selected at a time?
	// multi: multi-value input
	multiSelect: PropTypes.bool,

	// triggers when the fields loses focus
	onBlur: PropTypes.func,

	// what function to trigger when a change is detected
	// onChange handler: function(field, value(s)) {}
	// if multi-select then values is an array, otherwise it's a single value
	onChange: PropTypes.func,

	// options: array of Option class objects
	// [
	// 	{ value: 'one', label: 'One' },
	// 	{ value: 'two', label: 'Two' }
	// ]
	// passthrough
	options: function(props, propName, componentName) {
		let errorMessage = '';
		let missingValue = '';
		let missingLabel = '';
		let missingBoth = '';

		const missingValuesList = props.options.filter(item => item[props.valueKey] === undefined);
		if (missingValuesList.length !== 0) {
			missingValue = `valueKey("${props.valueKey}")`;
		}

		const missingLabelsList = props.options.filter(item => item[props.labelKey] === undefined);
		if (missingLabelsList.length !== 0) {
			missingLabel = `labelKey("${props.labelKey}")`;
		}

		if (missingValue && missingLabel) {
			missingBoth = ' and a ';
		}

		if (missingValue || missingLabel) {
			errorMessage = `${componentName}: a ${missingValue}${missingBoth}${missingLabel} could not be found in the "options" list.`;
		}

		if ((missingValue && missingLabel) && (props.labelKey === 'label' && props.valueKey === 'value')) {
			errorMessage += `
 			You must specify a prop for valueKey and labelKey if all options are not like this: { value: 'one', label: 'One' }`;
		}

		if (errorMessage) {
			return new Error(errorMessage);
		}
	},

	// input placeholder, displayed when there's no value
	// passthrough
	placeholder: PropTypes.string,

	// When the component mounts should it be given focus.
	// This behavior is dependent on ReactSelect
	setInitialFocus: PropTypes.bool,

	// should the label be shown?
	showLabel: PropTypes.bool,

	// is the list searchable?
	// searchable: whether to enable searching feature or not
	showSearch: PropTypes.bool,

	// css size style names: use Size.js presets
	// If no size is given a class of "resizable" will be added to the Select
	size: PropTypes.string,

	// sub-type of select
	// DEFAULT: normal select
	// CREATABLE: select that allows user to create options
	// future... ASYNC, ASYNCCREATABLE
	subType: PropTypes.oneOf([subTypes.DEFAULT,subTypes.CREATABLE]),

	// the validation rules and results
	validationEngine: PropTypes.instanceOf(ValidationEngine),

	// the initial value of this field - usually string or number
	// using "any" because it matches the ReactSelect component
	// passthrough
	// eslint-disable-next-line react/forbid-prop-types
	value: PropTypes.any,

	// value key so you can use your own key value pair
	// use with labelKey
	// default: "value"
	// passthrough
	valueKey: PropTypes.string,

	// MORE OPTIONS!!!!!
	// react-select has many more options
	// please see: https://github.com/JedWatson/react-select
	//
	// Filtering options:
	// https://github.com/JedWatson/react-select#filtering-options
};

const defaultProps = {
	allowClear: true,
	children: undefined,
	childrenPosition: ChildPosition.AFTER,
	className: undefined,
	dirtyEngine: undefined,
	disabled: false,
	errorMessage: undefined,
	escapeClearsValue: false,
	isDirty: undefined,
	labelKey: 'label',
	multiSelect: false,
	onBlur: undefined,
	onChange: undefined,
	placeholder: undefined,
	setInitialFocus: false,
	showLabel: true,
	showSearch: true,
	size: undefined,
	subType: subTypes.DEFAULT,
	validationEngine: new ValidationEngine(),
	value: undefined,
	valueKey: 'value',
};

export default class SelectInput extends React.Component {
	constructor(props) {
		super(props);

		this.onBlur = this.onBlur.bind(this);

		this.state = {
			hasBlurred: false
		};
		this.dirtyEngine = this.props.dirtyEngine ? this.props.dirtyEngine : new DirtyEngine({[this.props.field]: this.props.value});
	}

	onBlur() {
		this.setState({hasBlurred: true});
		if (this.props.onBlur) {
			this.props.onBlur(...arguments);
		}
	}

	render() {
		const validationError = this.props.validationEngine.getFormError(this.props.field, this.state.hasBlurred, this.props.errorMessage);
		const required = this.props.validationEngine.fieldHasRule(this.props.field, ValidationType.REQUIRED);

		const {
			allowClear,
			className,
			field,
			label,
			multiSelect,
			// need to handle onBlur here, otherwise the passThroughProps will clobber this.
			// eslint-disable-next-line no-unused-vars
			onBlur,
			onChange,
			setInitialFocus,
			showLabel,
			showSearch,
			size,
			...passThroughProps
		} = this.props;

		const selectProps = {
			'aria-labelledby': !showSearch ? `${field}__label` : undefined,
			autoFocus: setInitialFocus,
			className: joinClassNames(
				showLabel ? '' : 'no-label',
				size ? size : 'resizable',
				CommonAppSetup.getClassName(Components.SELECT_INPUT, 'input')
			),
			clearable: allowClear,
			id: field,
			multi: multiSelect,
			name: field,
			onBlur: this.onBlur,
			onChange: result => {
				let value = result;
				if (result) {
					if (_.isArray(result)) {
						// result comes in as an array of key/value pair objects so convert to just an array of values
						value = result.map(r => r[this.props.valueKey]);
					} else if (multiSelect) {
						// if multiselect, always return an array even if just one selected
						value = _.castArray(result[this.props.valueKey]);
					} else {
						// return just the value since not a multiselect
						value = result[this.props.valueKey];
					}
				}
				return onChange(this.props.field, value);
			},
			searchable: showSearch,
			...passThroughProps
		};

		return (
			<InputContainer className={joinClassNames(
				className,
				required ? 'required' : '',
				validationError ? 'error' : undefined,
				CommonAppSetup.getClassName(Components.SELECT_INPUT, 'wrapper'),
				this.dirtyEngine.isDirtyClassName({[this.props.field]: this.props.value}, this.props.field, this.props.isDirty)
			)}>
				{positionChildren(this, ChildPosition.BEFORE)}
				<label htmlFor={field} id={`${field}__label`} className={joinClassNames(
					showLabel ? '' : 'visually-hidden',
					CommonAppSetup.getClassName(Components.SELECT_INPUT, 'label'))
				}>
					{label}
				</label>
				{this.props.subType === subTypes.DEFAULT && <ReactSelect {...selectProps}/>}
				{this.props.subType === subTypes.CREATABLE && <ReactSelect.Creatable {...selectProps}/>}
				{positionChildren(this, ChildPosition.AFTER)}
				{validationError}
			</InputContainer>
		);
	}
}
SelectInput.subTypes = subTypes;
SelectInput.defaultProps = defaultProps;
SelectInput.propTypes = propTypes;